import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  ChatBubbleLeftIcon, 
  CpuChipIcon,
  DocumentMagnifyingGlassIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  HomeIcon,
  RocketLaunchIcon
} from '@heroicons/react/24/outline';

// Import tab components
import DashboardOverview from './dashboard/DashboardOverview';
import ChatManagement from './dashboard/ChatManagement';
import MemoryLogs from './dashboard/MemoryLogs';
import AgentAnalytics from './dashboard/AgentAnalytics';
import SystemAnalytics from './dashboard/SystemAnalytics';
import SettingsPanel from './dashboard/SettingsPanel';
import AutomationDashboard from './dashboard/AutomationDashboard'

export default function Dashboard() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    {
      id: 'overview',
      name: 'Overview',
      icon: HomeIcon,
      component: DashboardOverview,
      enabled: true,
      description: 'System overview and quick actions'
    },
    {
      id: 'chat',
      name: 'Chat Logs',
      icon: ChatBubbleLeftIcon,
      component: ChatManagement,
      enabled: user?.can_chat,
      description: 'Chat history and conversation management'
    },
    {
      id: 'memory',
      name: 'Memory Logs',
      icon: DocumentMagnifyingGlassIcon,
      component: MemoryLogs,
      enabled: user?.can_upload,
      description: 'Memory logs and content search'
    },
    {
      id: 'agents',
      name: 'Agents Logs',
      icon: CpuChipIcon,
      component: AgentAnalytics,
      enabled: user?.can_use_agents,
      description: 'Agent usage and performance analytics'
    },
    {
      id: 'analytics',
      name: 'Analytics',
      icon: ChartBarIcon,
      component: SystemAnalytics,
      enabled: user?.role === 'admin' || user?.can_use_agents,
      description: 'Usage analytics and insights'
    },
    {
      id: 'automation',
      name: 'Automation',
      icon: RocketLaunchIcon,
      component: AutomationDashboard,
      enabled: user?.role === 'admin',
      description: 'Everything about Weekly Automation in the system'
    },
    {
      id: 'settings',
      name: 'Settings',
      icon: Cog6ToothIcon,
      component: SettingsPanel,
      enabled: true,
      description: 'User preferences and system settings'
    }
  ];

  const enabledTabs = tabs.filter(tab => tab.enabled);
  const ActiveComponent = enabledTabs.find(tab => tab.id === activeTab)?.component || DashboardOverview;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Dashboard
              </h1>
              <p className="text-sm text-gray-600">
                Welcome back, {user?.full_name || user?.username}
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <div className="bg-gradient-to-r from-pink-600 to-purple-600 text-white px-3 py-1 rounded-lg text-sm font-medium">
                Phase 5
              </div>
              <div className="text-xs text-gray-500">
                Enhanced Dashboard
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="-mb-px flex space-x-8 overflow-x-auto">
            {enabledTabs.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors duration-200 ${
                    isActive
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon
                    className={`mr-2 h-5 w-5 ${
                      isActive ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-500'
                    }`}
                  />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="min-h-[600px]">
          <ActiveComponent user={user} />
        </div>
      </div>

      {/* Tab Description */}
      <div className="fixed bottom-4 right-4 max-w-sm">
        <div className="bg-black bg-opacity-75 text-white p-3 rounded-lg text-sm">
          {enabledTabs.find(tab => tab.id === activeTab)?.description}
        </div>
      </div>
    </div>
  );
}